import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { Button } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { useTrackRecentPlaylistChanges } from '../../hooks/useTrackRecentPlaylistChanges';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useEntityIdParam } from '../../hooks/useEntityIdParam';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { EmptyResults } from '../EmptyResults/EmptyResults';
import { SMLLink } from '../SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { IntegrationTypeIcon } from '../IntegrationTypeIcon';
import { PlaylistImageMosaic } from '../PlaylistImageMosaic/PlaylistImageMosaic';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';
import styles from './TrackRecentPlaylistChangesCard.module.scss';

const formatFollowers = (count: number, intl: ReturnType<typeof useIntl>): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return intl.formatNumber(count);
};

const TrackRecentPlaylistChangesCardComponent: React.FC = () => {
  const intl = useIntl();
  const trackId = useEntityIdParam('track');
  const { selectedDate, rangeDays } = useSMLDatePickerDateParam();
  const [extraDays, setExtraDays] = useState(0);
  // Reset the "load more" counter whenever the date picker range or anchor
  // changes, so the card always starts at the picker's current window.
  useEffect(() => {
    setExtraDays(0);
  }, [rangeDays, selectedDate]);
  const days = rangeDays + extraDays;

  const { data, isLoading, isFetching, error, refetch } = useTrackRecentPlaylistChanges({
    trackId: trackId ?? '',
    endDate: selectedDate,
    days,
  });

  const dayBuckets = useMemo(() => data?.days ?? [], [data?.days]);
  const isEmpty = !isLoading && dayBuckets.length === 0;

  const handleLoadMore = useCallback(() => {
    setExtraDays((prev) => prev + rangeDays);
  }, [rangeDays]);

  const subtitle = useMemo(() => {
    if (!data?.window) {
      return undefined;
    }
    const start = date.parse(data.window.startDate, 'YYYY-MM-DD');
    const end = date.parse(data.window.endDate, 'YYYY-MM-DD');
    return `${date.format(start, 'MMM DD')} – ${date.format(end, 'MMM DD, YYYY')}`;
  }, [data?.window]);

  return (
    <Panel>
      <PanelHead
        titleAs="h2"
        title={intl.formatMessage({
          id: 'app.sml.track.recentPlaylistChanges.title',
          defaultMessage: 'Recent playlist changes',
        })}
        subtitle={subtitle}
      />
      <PanelBody>
        <div className={`d-flex flex-column ${styles.cardContent}`}>
          {error && (
            <ErrorView
              error={error}
              retry={() => {
                refetch().catch(console.error);
              }}
            />
          )}
          {isLoading && dayBuckets.length === 0 && <LoadingView />}
          {!error && isEmpty && <EmptyResults />}
          {dayBuckets.map((day) => {
            const parsed = date.parse(day.date, 'YYYY-MM-DD');
            return (
              <div key={day.date}>
                <div className={styles.dayHeader}>{date.format(parsed, 'MMM DD, YYYY')}</div>
                {day.changes.map((change) => (
                  <SMLLink
                    key={`${day.date}-${change.playlistId}-${change.type}`}
                    to={SMLRoutes.playlistDetails}
                    params={{ playlistId: change.playlistId, [datePickerParamName]: day.date }}
                    className={`${styles.changeRow} d-flex justify-content-between align-items-center py-2 border-bottom text-decoration-none`}
                    style={{ color: 'inherit' }}
                  >
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                      <PlaylistImageMosaic
                        trackImageUrls={change.trackImageUrls}
                        fallbackImageUrl={change.imageUrl}
                        name={change.name}
                        size={32}
                      />
                      <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
                        <span className={styles.changeName}>{change.name}</span>
                        <span className={styles.followers}>
                          {intl.formatMessage(
                            {
                              id: 'app.sml.track.recentPlaylistChanges.followersAndPosition',
                              defaultMessage: '{followers} followers · pos. {position}',
                            },
                            { followers: formatFollowers(change.followersCount, intl), position: change.position + 1 }
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                      <IntegrationTypeIcon integrationType={change.integrationType} size={20} />
                      <span className={change.type === 'songAdded' ? styles.typeBadgeAdded : styles.typeBadgeRemoved}>
                        {change.type === 'songAdded'
                          ? intl.formatMessage({
                              id: 'app.sml.track.recentPlaylistChanges.added',
                              defaultMessage: 'Added',
                            })
                          : intl.formatMessage({
                              id: 'app.sml.track.recentPlaylistChanges.removed',
                              defaultMessage: 'Removed',
                            })}
                      </span>
                    </div>
                  </SMLLink>
                ))}
              </div>
            );
          })}
        </div>

        {!isEmpty && (
          <div className="border-top pt-3 mt-2 d-flex justify-content-center">
            <Button color="link" size="sm" onClick={handleLoadMore} disabled={isFetching}>
              {isFetching
                ? intl.formatMessage({
                    id: 'app.sml.track.recentPlaylistChanges.loading',
                    defaultMessage: 'Loading…',
                  })
                : intl.formatMessage({
                    id: 'app.sml.track.recentPlaylistChanges.loadMore',
                    defaultMessage: 'Load more',
                  })}
            </Button>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const TrackRecentPlaylistChangesCard = TypedMemo(TrackRecentPlaylistChangesCardComponent);
